import { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  CreditCard, Upload, FileText, RefreshCw, CheckCircle2,
  AlertTriangle, XCircle, Search, Plus, ChevronDown, Printer, FileSpreadsheet,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { exportToCSV, printTable } from '../../lib/printUtils';

const fmt = (n) => Math.round(n || 0).toLocaleString('es-CL');

const TABS = [
  { id: 'kpis', label: 'Resumen', icon: CheckCircle2 },
  { id: 'pendientes_vb', label: 'Pendientes VB', icon: AlertTriangle },
  { id: 'sin_match_movimiento', label: 'Sin match (banco)', icon: Search },
  { id: 'ocs_sin_movimiento', label: 'OCs sin movimiento', icon: FileText },
  { id: 'conciliados', label: 'Conciliados', icon: CheckCircle2 },
  { id: 'rechazados', label: 'Rechazados', icon: XCircle },
];

export default function TarjetasConciliacionPage() {
  const [tarjetas, setTarjetas] = useState([]);
  const [tarjetaCodigo, setTarjetaCodigo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('kpis');
  const [showUpload, setShowUpload] = useState(false);

  // Cargar tarjetas
  useEffect(() => {
    api.get('/tarjetas-pago').then((r) => {
      setTarjetas(r.data || []);
      if (r.data?.length && !tarjetaCodigo) setTarjetaCodigo(r.data[0].codigo);
    }).catch(() => {});
  // eslint-disable-next-line
  }, []);

  const loadConciliacion = useCallback(async () => {
    if (!tarjetaCodigo) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/tarjetas-conciliacion/${tarjetaCodigo}`);
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error cargando conciliación');
    }
    setLoading(false);
  }, [tarjetaCodigo]);

  useEffect(() => { loadConciliacion(); }, [loadConciliacion]);

  // Carlos (09/05/2026): Imprimir / Exportar conciliación según tab activo
  const tabLabel = TABS.find(t => t.id === tab)?.label || tab;
  const getTabRows = () => {
    if (!data) return [];
    if (tab === 'pendientes_vb') return data.pendientes_vb || [];
    if (tab === 'sin_match_movimiento') return data.sin_match_movimiento || [];
    if (tab === 'ocs_sin_movimiento') return data.ocs_sin_movimiento || [];
    if (tab === 'conciliados') return data.conciliados || [];
    if (tab === 'rechazados') return data.rechazados || [];
    return [];
  };

  const tarjetaSel = tarjetas.find(t => t.codigo === tarjetaCodigo);

  const ocColumns = [
    { key: 'numero_oc', label: 'OC', mono: true, align: 'center' },
    { key: 'fecha_creacion', label: 'Fecha OC', format: (v) => v ? new Date(v).toLocaleDateString('es-CL') : '' },
    { key: 'razon_social_proveedor', label: 'Proveedor' },
    { key: 'subtotal_total', label: 'Total OC', align: 'right', mono: true, format: (v) => `$${fmt(v)}` },
    { key: 'fecha_movimiento', label: 'Fecha banco', format: (v) => v ? new Date(v).toLocaleDateString('es-CL') : '' },
    { key: 'monto_movimiento', label: 'Monto banco', align: 'right', mono: true, format: (v) => v != null ? `$${fmt(v)}` : '-' },
    { key: 'diferencia', label: 'Diferencia', align: 'right', mono: true, format: (v) => v != null ? `$${fmt(v)}` : '-', color: undefined },
    { key: 'estado_conciliacion', label: 'Estado', align: 'center', format: (v) => (v || '').toUpperCase() },
  ];

  const movColumns = [
    { key: 'fecha', label: 'Fecha', format: (v) => v ? new Date(v).toLocaleDateString('es-CL') : '' },
    { key: 'descripcion', label: 'Descripción' },
    { key: 'monto', label: 'Monto', align: 'right', mono: true, format: (v) => `$${fmt(v)}` },
    { key: 'estado_match', label: 'Match', align: 'center' },
  ];

  const imprimirConciliacion = () => {
    const rows = getTabRows();
    if (!rows.length) { toast.warning(`No hay datos en "${tabLabel}" para imprimir`); return; }
    const isMov = tab === 'sin_match_movimiento';
    printTable({
      title: `Conciliación Cartolas · ${tabLabel}`,
      subtitle: `Tarjeta ${tarjetaSel?.etiqueta || tarjetaCodigo} · ${rows.length} registros · ${new Date().toLocaleString('es-CL')}`,
      summary: data?.kpis ? [
        { label: 'OCs sistema', value: `${data.kpis.ocs_total} · $${fmt(data.kpis.ocs_monto)}` },
        { label: 'Movimientos', value: `${data.kpis.movimientos_total} · $${fmt(data.kpis.movimientos_monto)}` },
        { label: 'Conciliados', value: data.kpis.conciliados, color: '#047857' },
        { label: 'Δ banco − sistema', value: `$${fmt(data.kpis.diferencia_monto)}`, color: data.kpis.diferencia_monto === 0 ? '#047857' : '#b45309', highlight: true },
      ] : [],
      columns: isMov ? movColumns : ocColumns,
      rows,
    });
  };

  const exportarConciliacionExcel = () => {
    const rows = getTabRows();
    if (!rows.length) { toast.warning(`No hay datos en "${tabLabel}" para exportar`); return; }
    const isMov = tab === 'sin_match_movimiento';
    exportToCSV({
      filename: `conciliacion_${tarjetaCodigo}_${tab}`,
      title: `CHIRINO STEEL · Conciliación · ${tabLabel} · Tarjeta ${tarjetaSel?.etiqueta || tarjetaCodigo}`,
      summary: data?.kpis ? [
        { label: 'Tarjeta', value: tarjetaSel?.etiqueta || tarjetaCodigo },
        { label: 'OCs total', value: data.kpis.ocs_total },
        { label: 'Monto OCs (CLP)', value: Math.round(data.kpis.ocs_monto) },
        { label: 'Movimientos banco', value: data.kpis.movimientos_total },
        { label: 'Monto banco (CLP)', value: Math.round(data.kpis.movimientos_monto) },
        { label: 'Conciliados', value: data.kpis.conciliados },
        { label: 'Pendientes VB', value: data.kpis.pendientes_vb },
        { label: 'Diferencia banco-sistema (CLP)', value: Math.round(data.kpis.diferencia_monto) },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ] : [],
      columns: isMov ? [
        { key: 'fecha', label: 'Fecha', format: (v) => v ? new Date(v).toLocaleDateString('es-CL') : '' },
        { key: 'descripcion', label: 'Descripción' },
        { key: 'monto', label: 'Monto (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'estado_match', label: 'Estado match' },
        { key: 'cartola_id', label: 'Cartola ID' },
      ] : [
        { key: 'numero_oc', label: 'N. OC' },
        { key: 'fecha_creacion', label: 'Fecha OC', format: (v) => v ? new Date(v).toLocaleDateString('es-CL') : '' },
        { key: 'razon_social_proveedor', label: 'Proveedor' },
        { key: 'rut_proveedor', label: 'RUT' },
        { key: 'subtotal_total', label: 'Total OC (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'fecha_movimiento', label: 'Fecha banco', format: (v) => v ? new Date(v).toLocaleDateString('es-CL') : '' },
        { key: 'monto_movimiento', label: 'Monto banco (CLP)', format: (v) => v != null ? Math.round(Number(v) || 0) : '' },
        { key: 'diferencia', label: 'Diferencia (CLP)', format: (v) => v != null ? Math.round(Number(v) || 0) : '' },
        { key: 'estado_conciliacion', label: 'Estado conciliación', format: (v) => (v || '').toUpperCase() },
        { key: 'observacion', label: 'Observación' },
      ],
      rows,
    });
  };

  return (
    <div className="p-6 max-w-[1500px] mx-auto" data-testid="conciliacion-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-indigo-600" />
            Conciliación de Cartolas
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Subí cartolas (parciales o final) y el sistema cruza con OCs Express. Las discrepancias requieren VB.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={tarjetaCodigo}
            onChange={(e) => setTarjetaCodigo(e.target.value)}
            className="border rounded px-3 py-1.5 text-sm bg-white"
            data-testid="select-tarjeta"
          >
            {tarjetas.map((t) => (
              <option key={t.codigo} value={t.codigo}>
                {t.favorita ? '★ ' : ''}{t.etiqueta} ({t.codigo})
              </option>
            ))}
          </select>
          <Button
            onClick={() => setShowUpload(true)}
            disabled={!tarjetaCodigo}
            className="gap-1.5"
            data-testid="btn-upload-cartola"
          >
            <Upload className="w-4 h-4" /> Subir cartola
          </Button>
          <Button
            variant="outline"
            onClick={loadConciliacion}
            disabled={loading}
            className="gap-1.5"
            data-testid="btn-refresh-conc"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refrescar
          </Button>
          {data && (
            <>
              <Button variant="outline" size="sm" onClick={imprimirConciliacion} className="gap-1.5" title="Imprimir tab actual" data-testid="btn-print-concilia">
                <Printer className="w-4 h-4" /> Imprimir
              </Button>
              <Button variant="outline" size="sm" onClick={exportarConciliacionExcel} className="gap-1.5 text-emerald-700 border-emerald-300 hover:bg-emerald-50" title="Exportar tab actual a Excel/CSV" data-testid="btn-excel-concilia">
                <FileSpreadsheet className="w-4 h-4" /> Excel
              </Button>
            </>
          )}
        </div>
      </div>

      {data && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mb-4">
            <KPI label="Movimientos" value={data.kpis.movimientos_total} sub={`$${fmt(data.kpis.movimientos_monto)}`} color="indigo" testId="kpi-movs" />
            <KPI label="OCs sistema" value={data.kpis.ocs_total} sub={`$${fmt(data.kpis.ocs_monto)}`} color="indigo" testId="kpi-ocs" />
            <KPI label="Conciliados" value={data.kpis.conciliados} color="emerald" testId="kpi-conc" />
            <KPI label="Pendientes VB" value={data.kpis.pendientes_vb} color="amber" testId="kpi-vb" />
            <KPI label="Sin match (banco)" value={data.kpis.sin_match_movimiento} color="red" testId="kpi-sin-match-mov" />
            <KPI label="OCs sin mov." value={data.kpis.ocs_sin_movimiento} color="red" testId="kpi-ocs-sin-mov" />
            <KPI label="Δ banco − sistema" value={`$${fmt(data.kpis.diferencia_monto)}`} color={data.kpis.diferencia_monto === 0 ? 'emerald' : 'amber'} testId="kpi-diff" />
          </div>

          {/* Tabs */}
          <div className="flex gap-1 border-b mb-3 overflow-x-auto">
            {TABS.map((t) => {
              const Icon = t.icon;
              const count = t.id === 'kpis' ? null : (data.buckets[t.id]?.length ?? 0);
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 whitespace-nowrap transition ${
                    tab === t.id ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-800'
                  }`}
                  data-testid={`tab-${t.id}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {t.label}
                  {count !== null && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${count > 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Content */}
          {tab === 'kpis' && <ResumenView data={data} />}
          {tab !== 'kpis' && (
            <BucketTable
              tab={tab}
              items={data.buckets[tab] || []}
              tarjetaCodigo={tarjetaCodigo}
              onChange={loadConciliacion}
            />
          )}
        </>
      )}

      {showUpload && (
        <UploadDialog
          open={showUpload}
          onClose={() => setShowUpload(false)}
          onUploaded={() => { setShowUpload(false); loadConciliacion(); }}
          tarjetaCodigo={tarjetaCodigo}
          tarjeta={tarjetas.find((t) => t.codigo === tarjetaCodigo)}
        />
      )}
    </div>
  );
}

function KPI({ label, value, sub, color, testId }) {
  const colors = {
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    red: 'bg-red-50 border-red-200 text-red-700',
  };
  return (
    <div className={`p-3 rounded border ${colors[color]}`} data-testid={testId}>
      <div className="text-xl font-bold">{value}</div>
      <div className="text-[10px] text-gray-600 mt-0.5">{label}</div>
      {sub && <div className="text-[10px] text-gray-500 font-mono">{sub}</div>}
    </div>
  );
}

function ResumenView({ data }) {
  const ratio = data.kpis.ocs_total > 0
    ? Math.round((data.kpis.conciliados / data.kpis.ocs_total) * 100)
    : 0;
  return (
    <div className="space-y-4">
      <div className="border rounded-lg p-4 bg-white">
        <div className="text-sm font-semibold mb-2">Estado general</div>
        <div className="text-xs text-gray-600 mb-3">
          Rango: <strong>{data.rango.desde}</strong> → <strong>{data.rango.hasta}</strong>
        </div>
        <div className="space-y-2">
          <ProgressLine label="Conciliados" value={data.kpis.conciliados} total={data.kpis.ocs_total} color="emerald" />
          <ProgressLine label="Pendientes VB" value={data.kpis.pendientes_vb} total={data.kpis.movimientos_total} color="amber" />
          <ProgressLine label="OCs sin movimiento bancario" value={data.kpis.ocs_sin_movimiento} total={data.kpis.ocs_total} color="red" />
        </div>
        {ratio === 100 && (
          <div className="mt-3 p-2 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-800">
            <CheckCircle2 className="w-4 h-4 inline mr-1" />
            ¡Conciliación completa al 100%!
          </div>
        )}
      </div>
    </div>
  );
}

function ProgressLine({ label, value, total, color }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  const bar = { emerald: 'bg-emerald-500', amber: 'bg-amber-500', red: 'bg-red-500' }[color];
  return (
    <div>
      <div className="flex justify-between text-xs mb-0.5">
        <span className="text-gray-700">{label}</span>
        <span className="font-semibold">{value} / {total} ({pct.toFixed(0)}%)</span>
      </div>
      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full ${bar}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function BucketTable({ tab, items, tarjetaCodigo, onChange }) {
  const isOcsBucket = tab === 'ocs_sin_movimiento';
  if (items.length === 0) {
    return <div className="border rounded-lg p-6 text-center text-gray-500 text-sm">Sin items en esta categoría 🎉</div>;
  }

  if (isOcsBucket) {
    return (
      <div className="border rounded-lg overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">N° OC</th>
              <th className="px-3 py-2 text-left">Fecha</th>
              <th className="px-3 py-2 text-left">Proveedor</th>
              <th className="px-3 py-2 text-right">Monto neto</th>
              <th className="px-3 py-2 text-left">OT</th>
              <th className="px-3 py-2 text-left">Origen</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {items.map((oc) => (
              <tr key={oc.numero_oc} data-testid={`oc-${oc.numero_oc}`}>
                <td className="px-3 py-2 font-mono font-semibold">{oc.numero_oc}</td>
                <td className="px-3 py-2 text-xs">{(oc.fecha_creacion || '').slice(0, 10)}</td>
                <td className="px-3 py-2 text-xs">{oc.razon_social_proveedor || oc.rut_proveedor}</td>
                <td className="px-3 py-2 text-right font-mono">${fmt(oc.neto)}</td>
                <td className="px-3 py-2 text-xs">{oc.ot_numero || '-'}</td>
                <td className="px-3 py-2 text-xs"><span className="text-[10px] px-1.5 py-0.5 bg-gray-100 rounded">{oc.source}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-xs uppercase text-gray-500">
          <tr>
            <th className="px-3 py-2 text-left">Fecha</th>
            <th className="px-3 py-2 text-left">Descripción / Lugar</th>
            <th className="px-3 py-2 text-right">Monto</th>
            <th className="px-3 py-2 text-center">Match</th>
            <th className="px-3 py-2 text-center">VB</th>
            <th className="px-3 py-2 text-center">Acciones</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {items.map((m) => (
            <MovimientoRow key={m.id} mov={m} tarjetaCodigo={tarjetaCodigo} onChange={onChange} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MovimientoRow({ mov, tarjetaCodigo, onChange }) {
  const [showMatch, setShowMatch] = useState(false);
  const [showVB, setShowVB] = useState(false);

  const matchBadge = !mov.oc_match_id ? (
    <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded">Sin OC</span>
  ) : mov.match_method === 'manual' ? (
    <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded font-mono">👤 OC {mov.oc_match_id}</span>
  ) : (
    <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-mono" title={`score ${mov.match_score}`}>🤖 OC {mov.oc_match_id}</span>
  );

  // Iter 313.b — Confianza del matching bancario (Alta/Media/Baja/Manual)
  const confBadge = (() => {
    if (!mov.oc_match_id) return null;
    const c = mov.match_confidence;
    const cfg = {
      alta:   { bg: 'bg-green-100  text-green-800  border-green-300',  label: 'Confianza Alta',  dot: 'bg-green-500' },
      media:  { bg: 'bg-yellow-100 text-yellow-800 border-yellow-300', label: 'Confianza Media', dot: 'bg-yellow-500' },
      baja:   { bg: 'bg-red-100    text-red-700    border-red-300',    label: 'Confianza Baja',  dot: 'bg-red-500' },
      manual: { bg: 'bg-purple-50  text-purple-700 border-purple-200', label: 'Match manual',    dot: 'bg-purple-500' },
    }[c];
    if (!cfg) return null;
    return (
      <span
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${cfg.bg}`}
        title={`${cfg.label} — score ${mov.match_score ?? '?'}`}
        data-testid={`badge-confianza-${mov.id}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`}></span>
        {cfg.label}
      </span>
    );
  })();

  const vbBadge = {
    aprobada: <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded">✓ Aprobada</span>,
    aprobada_auto: <span className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded">✓ Auto</span>,
    pendiente: <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">⏳ Pendiente</span>,
    rechazada: <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded">✗ Rechazada</span>,
    no_requiere: <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">—</span>,
  }[mov.vb_estado] || null;

  return (
    <>
      <tr data-testid={`row-mov-${mov.id}`}>
        <td className="px-3 py-2 text-xs whitespace-nowrap">{mov.fecha}</td>
        <td className="px-3 py-2 text-xs">
          <div className="font-medium">{mov.descripcion}</div>
          {mov.lugar && <div className="text-gray-500">{mov.lugar}</div>}
          {mov.cuotas && <div className="text-blue-600 text-[10px]">Cuotas: {mov.cuotas}</div>}
        </td>
        <td className="px-3 py-2 text-right font-mono font-semibold">${fmt(mov.monto)}</td>
        <td className="px-3 py-2 text-center">
          <div className="inline-flex flex-col items-center gap-0.5">
            {matchBadge}
            {confBadge}
          </div>
        </td>
        <td className="px-3 py-2 text-center">{vbBadge}</td>
        <td className="px-3 py-2 text-center">
          <div className="flex gap-1 justify-center">
            <button
              onClick={() => setShowMatch(true)}
              className="text-[10px] px-2 py-1 border rounded hover:bg-gray-50"
              data-testid={`btn-match-${mov.id}`}
            >
              Match manual
            </button>
            {mov.vb_estado === 'pendiente' && (
              <button
                onClick={() => setShowVB(true)}
                className="text-[10px] px-2 py-1 bg-amber-500 text-white rounded hover:bg-amber-600"
                data-testid={`btn-vb-${mov.id}`}
              >
                VB
              </button>
            )}
          </div>
        </td>
      </tr>
      {showMatch && (
        <ManualMatchDialog
          mov={mov}
          tarjetaCodigo={tarjetaCodigo}
          onClose={() => setShowMatch(false)}
          onDone={() => { setShowMatch(false); onChange?.(); }}
        />
      )}
      {showVB && (
        <VBDialog
          mov={mov}
          onClose={() => setShowVB(false)}
          onDone={() => { setShowVB(false); onChange?.(); }}
        />
      )}
    </>
  );
}

function UploadDialog({ open, onClose, onUploaded, tarjetaCodigo, tarjeta }) {
  const [tipoCartola, setTipoCartola] = useState('parcial');
  const [pasted, setPasted] = useState('');
  const [parsed, setParsed] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Parse texto pegado (formato típico: fecha desc/lugar monto)
  const parsePasted = () => {
    const lines = pasted.split('\n').map((l) => l.trim()).filter(Boolean);
    const movs = [];
    for (const line of lines) {
      // Dividir por TAB primero, luego por 2+ espacios
      let parts = line.split('\t').filter(Boolean);
      if (parts.length < 3) parts = line.split(/\s{2,}/).filter(Boolean);
      if (parts.length < 3) continue;
      // Buscar fecha (DD/MM o DD-MM o YYYY-MM-DD)
      const fechaIdx = parts.findIndex((p) => /\d{1,4}[/-]\d{1,2}([/-]\d{1,4})?/.test(p));
      if (fechaIdx === -1) continue;
      // Monto: último número con $ o coma
      const montoIdx = [...parts].reverse().findIndex((p) => /[\d,.]+/.test(p) && /[$\d]/.test(p));
      if (montoIdx === -1) continue;
      const realMontoIdx = parts.length - 1 - montoIdx;
      const fecha = parts[fechaIdx];
      const montoRaw = parts[realMontoIdx].replace(/[^\d,-]/g, '').replace(/\./g, '').replace(',', '.');
      const monto = parseFloat(montoRaw);
      if (!monto || isNaN(monto)) continue;
      // Descripción/Lugar: lo que está entre fecha y monto
      const between = parts.slice(fechaIdx + 1, realMontoIdx).join(' ');
      movs.push({
        fecha,
        descripcion: between || parts[fechaIdx + 1] || '',
        lugar: '',
        monto: Math.abs(monto),
      });
    }
    setParsed(movs);
    if (movs.length === 0) toast.warning('No se detectaron movimientos. Verifica el formato (fecha descripción monto).');
    else toast.success(`${movs.length} movimientos detectados`);
  };

  const doUpload = async () => {
    if (parsed.length === 0) {
      toast.error('Pegá la cartola y procesá primero');
      return;
    }
    setUploading(true);
    try {
      const r = await api.post('/admin/tarjetas-conciliacion/upload', {
        tarjeta_codigo: tarjetaCodigo,
        tipo_cartola: tipoCartola,
        nombre_archivo: `manual_paste_${new Date().toISOString().slice(0, 10)}`,
        movimientos: parsed,
      });
      const d = r.data;
      toast.success(`✅ ${d.matched_count} match auto, ${d.pendientes_count} pendientes. Total: $${fmt(d.total_monto)}`);
      onUploaded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al subir');
    }
    setUploading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Subir Cartola — {tarjeta?.etiqueta}</DialogTitle>
          <DialogDescription>
            Pegá los movimientos del banco (1 por línea: fecha, descripción/lugar, monto).
            Acumulativo: cargas previas se conservan, los duplicados se actualizan.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold">Tipo de cartola</label>
            <div className="flex gap-2 mt-1">
              <button
                onClick={() => setTipoCartola('parcial')}
                className={`px-3 py-1.5 text-xs rounded border ${
                  tipoCartola === 'parcial' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white border-gray-300'
                }`}
                data-testid="tipo-parcial"
              >
                📋 Parcial (movimientos no facturados)
              </button>
              <button
                onClick={() => setTipoCartola('final')}
                className={`px-3 py-1.5 text-xs rounded border ${
                  tipoCartola === 'final' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white border-gray-300'
                }`}
                data-testid="tipo-final"
              >
                📑 Final (cartola facturada)
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold">Pegá los movimientos</label>
            <textarea
              value={pasted}
              onChange={(e) => setPasted(e.target.value)}
              rows={10}
              className="w-full mt-1 border rounded p-2 text-xs font-mono"
              placeholder={`Ejemplos:\n06/05/2026\tPRODALAM SA\tSTGO\t147322\n03/05/2026\tFERRETERIA XYZ\t50.000\n2026-05-01\tCOPEC\tSantiago\t$35,000`}
              data-testid="textarea-pasted"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={parsePasted}
              className="mt-2 gap-1"
              data-testid="btn-parse"
            >
              <RefreshCw className="w-3 h-3" /> Procesar texto
            </Button>
          </div>

          {parsed.length > 0 && (
            <div className="border rounded p-2 bg-gray-50 max-h-60 overflow-y-auto">
              <div className="text-xs font-semibold mb-1">{parsed.length} movimientos detectados:</div>
              <table className="w-full text-xs">
                <thead className="text-gray-500"><tr><th className="text-left">Fecha</th><th className="text-left">Descripción</th><th className="text-right">Monto</th></tr></thead>
                <tbody>
                  {parsed.map((m, i) => (
                    <tr key={i} className="border-t border-gray-200">
                      <td>{m.fecha}</td>
                      <td className="truncate max-w-[300px]">{m.descripcion}</td>
                      <td className="text-right font-mono">${fmt(m.monto)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={doUpload} disabled={parsed.length === 0 || uploading} data-testid="btn-confirm-upload">
            {uploading ? 'Subiendo...' : `Subir ${parsed.length} mov.`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManualMatchDialog({ mov, tarjetaCodigo, onClose, onDone }) {
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (numeroOc) => {
    setSubmitting(true);
    try {
      await api.post(`/admin/tarjetas-conciliacion/movimiento/${mov.id}/match`, {
        numero_oc: numeroOc,
      });
      toast.success(numeroOc ? `Vinculado a OC ${numeroOc}` : 'Desvinculado');
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar match');
    }
    setSubmitting(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Match manual</DialogTitle>
          <DialogDescription>
            Movimiento: <strong>{mov.descripcion}</strong> · ${fmt(mov.monto)} · {mov.fecha}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Número de OC (ej: 18700)"
            className="w-full border rounded px-3 py-2 text-sm"
            data-testid="input-numero-oc"
          />
          {mov.oc_match_id && (
            <Button variant="outline" onClick={() => submit(null)} disabled={submitting} className="w-full gap-1 border-red-300 text-red-700">
              Desvincular OC {mov.oc_match_id}
            </Button>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => submit(search.trim())} disabled={!search.trim() || submitting} data-testid="btn-confirm-match">
            Vincular
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VBDialog({ mov, onClose, onDone }) {
  const [obs, setObs] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (decision) => {
    setSubmitting(true);
    try {
      await api.post(`/admin/tarjetas-conciliacion/movimiento/${mov.id}/vb`, {
        decision,
        observacion: obs,
      });
      toast.success(decision === 'aprobar' ? '✅ Aprobado' : '❌ Rechazado');
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar VB');
    }
    setSubmitting(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Visto Bueno (VB)</DialogTitle>
          <DialogDescription>
            <strong>{mov.descripcion}</strong> · ${fmt(mov.monto)} · {mov.fecha}<br />
            {mov.oc_match_id ? `Match: OC ${mov.oc_match_id}` : 'Sin match en sistema'}
          </DialogDescription>
        </DialogHeader>
        <textarea
          value={obs}
          onChange={(e) => setObs(e.target.value)}
          placeholder="Observación (opcional)"
          rows={3}
          className="w-full border rounded p-2 text-sm"
          data-testid="textarea-vb-obs"
        />
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button variant="outline" onClick={() => submit('rechazar')} disabled={submitting} className="border-red-300 text-red-700" data-testid="btn-vb-rechazar">
            Rechazar
          </Button>
          <Button onClick={() => submit('aprobar')} disabled={submitting} className="bg-emerald-600" data-testid="btn-vb-aprobar">
            Aprobar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
